import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, CardHeader, Table, Badge, Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { DatosSesion } from "../../Components/Common/DatosSesion";
import { getDiagnosticosFarmacologicos } from "../../helpers/backend_helper";
import WidgetLoader from "./WidgetLoader";

const DiagnosticosFarmacologicos = React.memo(({ fechaInicio, fechaFin, codigoProveedor, aseguradora }) => {
  const [diagnosticos, setDiagnosticos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [modalAyuda, setModalAyuda] = useState(false);
  const primeraVezRef = useRef(true);
  const timeoutRef = useRef(null);

  //console.log("🔄 DiagnosticosFarmacologicos render - diagnosticos.length:", diagnosticos.length);

  useEffect(() => {
    // Validar que tengamos fechas
    if (!fechaInicio || !fechaFin) {
      console.warn("DiagnosticosFarmacologicos - Faltan fechas:", { fechaInicio, fechaFin });
      return;
    }
    
    // Si es la primera vez y es proveedor sin código, esperar
    if (primeraVezRef.current) {
      const perfil = DatosSesion("perfil") || "";
      const esAdministrador = perfil === "Administrador" || perfil === "MyHealth";
      
      if (!esAdministrador && codigoProveedor === null) {
        //console.log("⏳ DiagnosticosFarmacologicos - Esperando código de proveedor...");
        return;
      }
      
      primeraVezRef.current = false;
    }
    
    // Cancelar timeout anterior si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce: esperar 100ms antes de ejecutar
    timeoutRef.current = setTimeout(() => {
      //console.log("📊 DiagnosticosFarmacologicos - Cargando con:", { fechaInicio, fechaFin, codigoProveedor });
      cargarDiagnosticos();
    }, 100);
    
    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fechaInicio, fechaFin, codigoProveedor, aseguradora]);

  const cargarDiagnosticos = async () => {
    setCargando(true);
    setError(null);

    try {
      const params = {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        top: 5
      };

      if (codigoProveedor && codigoProveedor.trim() !== "") {
        params.codigo_proveedor = codigoProveedor;
      }

      if (aseguradora) {
        params.aseguradora = aseguradora;
      }

      const response = await getDiagnosticosFarmacologicos(params);
      //console.log("📊 Response completa:", response);
      //console.log("📊 response.data:", response.data);
      //console.log("📊 response.data.data:", response.data?.data);
      // APIClient devuelve { data: { data: [...], status_code, message } }
      const datos = response.data?.data || [];
      //console.log("📊 Datos a setear:", datos, "Length:", datos.length);
      setDiagnosticos(datos);
    } catch (err) {
      console.error("Error al cargar diagnósticos:", err);
      setError(err.message || "Error al cargar diagnósticos");
    } finally {
      setCargando(false);
    }
  };

  const formatearNumero = (numero) => {
    return numero.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getColorDesviacion = (porcentaje) => {
    if (porcentaje >= 50) return "#f06548"; // Rojo
    if (porcentaje >= 30) return "#f7b84b"; // Amarillo
    return "#0ab39c"; // Verde
  };

  return (
    <Card className="card-height-100">
      <CardHeader className="align-items-center d-flex">
        <h4 className="card-title mb-0 flex-grow-1">
          <i className="ri-medicine-bottle-line me-2"></i>
          Diagnósticos Farmacológicos (Top 5)
        </h4>
        <div className="flex-shrink-0 d-flex gap-2 align-items-center">
          <Badge color="info" className="fs-12">
            {diagnosticos.length} Diagnósticos
          </Badge>
          <Button
            color="light"
            size="sm"
            className="btn-icon"
            onClick={() => setModalAyuda(true)}
            title="Ayuda sobre cálculos"
          >
            <i className="ri-question-line"></i>
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        {cargando ? (
          <div className="table-responsive">
            <Table className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "25%" }}><span className="placeholder col-6"></span></th>
                  {[1, 2, 3, 4, 5].map(i => (
                    <th key={i} className="text-center" style={{ width: "15%" }}>
                      <div className="d-flex flex-column align-items-center placeholder-glow">
                        <span className="placeholder col-8 mb-1"></span>
                        <span className="placeholder col-4"></span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map(i => (
                  <tr key={i}>
                    <td>
                      <div className="d-flex align-items-center placeholder-glow">
                        <span className="avatar-xs rounded-circle placeholder me-2"></span>
                        <span className="placeholder col-8"></span>
                      </div>
                    </td>
                    {[1, 2, 3, 4, 5].map(j => (
                      <td key={j} className="text-center placeholder-glow">
                        <div className="d-flex flex-column align-items-center">
                           <span className="placeholder col-6 mb-1"></span>
                           {j === 1 || j === 5 ? <span className="placeholder col-12" style={{height: '4px'}}></span> : null}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            <i className="ri-error-warning-line me-2"></i>
            Error al cargar diagnósticos: {error}
          </div>
        ) : diagnosticos.length === 0 ? (
          <div className="text-center py-4">
            <i className="ri-file-list-line fs-1 text-muted"></i>
            <p className="text-muted mt-2 mb-0">No hay diagnósticos para mostrar</p>
          </div>

        ) : (
          <div className="table-responsive">
            <Table className="table-hover table-striped align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "25%" }}>Diagnóstico</th>
                  <th className="text-center" style={{ width: "15%" }}>
                    <div className="d-flex flex-column align-items-center">
                      <span>Frecuencia</span>
                      <small className="text-muted fw-normal">(% total)</small>
                    </div>
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    <div className="d-flex flex-column align-items-center">
                      <span>Total USD</span>
                      <small className="text-muted fw-normal">(facturado)</small>
                    </div>
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    <div className="d-flex flex-column align-items-center">
                      <span>Promedio</span>
                      <small className="text-muted fw-normal">(USD)</small>
                    </div>
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    <div className="d-flex flex-column align-items-center">
                      <span>Rango</span>
                      <small className="text-muted fw-normal">(Min - Max)</small>
                    </div>
                  </th>
                  <th className="text-center" style={{ width: "15%" }}>
                    <div className="d-flex flex-column align-items-center">
                      <span>Variación</span>
                      <small className="text-muted fw-normal">(Desv. %)</small>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {diagnosticos.map((diag, index) => (
                  <tr key={index}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0 me-2">
                          <div
                            className={`avatar-xs rounded-circle ${
                              index === 0
                                ? "bg-success-subtle"
                                : index === 1
                                ? "bg-info-subtle"
                                : index === 2
                                ? "bg-warning-subtle"
                                : "bg-secondary-subtle"
                            }`}
                          >
                            <span
                              className={`avatar-title rounded-circle fs-14 ${
                                index === 0
                                  ? "text-success"
                                  : index === 1
                                  ? "text-info"
                                  : index === 2
                                  ? "text-warning"
                                  : "text-secondary"
                              }`}
                            >
                              {index + 1}
                            </span>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <span className="fw-medium">{diag.nombre}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <Badge color="primary" className="fs-11">
                            {diag.frecuencia}
                          </Badge>
                          <span className="text-primary fw-medium fs-12">
                            {diag.porcentaje_frecuencia}%
                          </span>
                        </div>
                        <div className="progress progress-sm">
                          <div
                            className="progress-bar bg-primary"
                            role="progressbar"
                            style={{ width: `${diag.porcentaje_frecuencia}%` }}
                            aria-valuenow={diag.porcentaje_frecuencia}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className="fw-bold text-primary fs-14">
                        $ {formatearNumero(diag.costo_total_usd)}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="fw-medium text-success fs-13">
                        $ {formatearNumero(diag.costo_promedio_usd)}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex flex-column gap-1">
                        <small className="text-muted">
                          $ {formatearNumero(diag.costo_minimo_usd)}
                        </small>
                        <div style={{ borderTop: '1px solid #dee2e6' }}></div>
                        <small className="text-muted">
                          $ {formatearNumero(diag.costo_maximo_usd)}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column">
                        <div className="d-flex align-items-center justify-content-center mb-1">
                          <span 
                            className="fw-medium fs-13"
                            style={{ color: getColorDesviacion(diag.desviacion_std_porcentaje) }}
                          >
                            {diag.desviacion_std_porcentaje}%
                          </span>
                        </div>
                        <div className="progress progress-sm">
                          <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ 
                              width: `${Math.min(diag.desviacion_std_porcentaje, 100)}%`,
                              backgroundColor: getColorDesviacion(diag.desviacion_std_porcentaje)
                            }}
                            aria-valuenow={diag.desviacion_std_porcentaje}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {!cargando && !error && diagnosticos.length > 0 && (
          <div className="mt-3 p-2 bg-light rounded">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <small className="text-muted fw-medium">
                <i className="ri-information-line me-1"></i>
                Interpretación:
              </small>
              <div className="d-flex align-items-center gap-1">
                <div style={{ width: '12px', height: '12px', backgroundColor: '#0ab39c', borderRadius: '2px' }}></div>
                <small className="text-muted">Baja variación (&lt;30%)</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div style={{ width: '12px', height: '12px', backgroundColor: '#f7b84b', borderRadius: '2px' }}></div>
                <small className="text-muted">Media variación (30-50%)</small>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div style={{ width: '12px', height: '12px', backgroundColor: '#f06548', borderRadius: '2px' }}></div>
                <small className="text-muted">Alta variación (&gt;50%)</small>
              </div>
            </div>
          </div>
        )}
      </CardBody>

      {/* Modal de Ayuda */}
      <Modal isOpen={modalAyuda} toggle={() => setModalAyuda(!modalAyuda)} size="lg">
        <ModalHeader toggle={() => setModalAyuda(!modalAyuda)}>
          <i className="ri-information-line me-2 text-primary"></i>
          Cómo se Calculan los Diagnósticos Farmacológicos
        </ModalHeader>
        <ModalBody>
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-calculator-line me-2"></i>
              Descripción de Columnas
            </h5>
            
            <div className="table-responsive">
              <Table className="table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '25%' }}>Columna</th>
                    <th style={{ width: '35%' }}>Cálculo</th>
                    <th style={{ width: '40%' }}>Interpretación</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Frecuencia</strong></td>
                    <td>
                      Cantidad de facturas con este diagnóstico
                      <br />
                      <code className="text-muted">% = (facturas / total) × 100</code>
                    </td>
                    <td>Identifica qué diagnósticos son más comunes</td>
                  </tr>
                  <tr>
                    <td><strong>Total USD</strong></td>
                    <td>
                      Suma de todas las facturas
                      <br />
                      <code className="text-muted">Σ(montos_usd)</code>
                    </td>
                    <td>Impacto total del diagnóstico en facturación</td>
                  </tr>
                  <tr>
                    <td><strong>Promedio</strong></td>
                    <td>
                      Costo promedio por factura
                      <br />
                      <code className="text-muted">Total USD / Frecuencia</code>
                    </td>
                    <td>Costo típico esperado por factura</td>
                  </tr>
                  <tr>
                    <td><strong>Rango</strong></td>
                    <td>
                      Valores mínimo y máximo
                      <br />
                      <code className="text-muted">min(montos) - max(montos)</code>
                    </td>
                    <td>Amplitud de variación de precios</td>
                  </tr>
                  <tr>
                    <td><strong>Variación</strong></td>
                    <td>
                      Desviación estándar como %
                      <br />
                      <code className="text-muted">(desv_std / promedio) × 100</code>
                    </td>
                    <td>
                      Consistencia de precios:
                      <br />
                      <span className="text-success">• &lt;30%: Muy consistente</span>
                      <br />
                      <span className="text-warning">• 30-50%: Moderado</span>
                      <br />
                      <span className="text-danger">• &gt;50%: Alta variación</span>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-file-list-3-line me-2"></i>
              Ejemplo Práctico
            </h5>
            
            <div className="alert alert-info">
              <h6 className="alert-heading">
                <i className="ri-medicine-bottle-line me-2"></i>
                Diagnóstico: Hipertensión arterial
              </h6>
              <hr />
              <div className="row">
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>Frecuencia:</strong> 362 facturas (29.53%)
                    <br />
                    <small className="text-muted">→ Es el diagnóstico más común</small>
                  </p>
                  <p className="mb-2">
                    <strong>Total USD:</strong> $14,146.96
                    <br />
                    <small className="text-muted">→ El que más factura en total</small>
                  </p>
                  <p className="mb-2">
                    <strong>Promedio:</strong> $39.08
                    <br />
                    <small className="text-muted">→ Costo típico por factura</small>
                  </p>
                </div>
                <div className="col-md-6">
                  <p className="mb-2">
                    <strong>Rango:</strong> $5.00 - $150.00
                    <br />
                    <small className="text-muted">→ Amplitud de $145 (muy amplia)</small>
                  </p>
                  <p className="mb-2">
                    <strong>Variación:</strong> 86.23% <span className="badge bg-danger">Alta</span>
                    <br />
                    <small className="text-muted">→ Precios muy inconsistentes</small>
                  </p>
                </div>
              </div>
              <hr />
              <p className="mb-0">
                <strong>Conclusión:</strong> Aunque es el diagnóstico más frecuente y el que más factura, 
                tiene una <strong className="text-danger">alta variabilidad de precios</strong> que podría 
                indicar falta de estandarización o diferentes niveles de complejidad en los tratamientos.
              </p>
            </div>
          </div>

          <div className="mb-3">
            <h5 className="text-primary mb-3">
              <i className="ri-lightbulb-line me-2"></i>
              Recomendaciones de Análisis
            </h5>
            
            <ul className="list-unstyled">
              <li className="mb-2">
                <i className="ri-check-line text-success me-2"></i>
                <strong>Frecuencia alta + Variación baja:</strong> Diagnóstico bien estandarizado
              </li>
              <li className="mb-2">
                <i className="ri-alert-line text-warning me-2"></i>
                <strong>Frecuencia alta + Variación alta:</strong> Revisar causas de inconsistencia
              </li>
              <li className="mb-2">
                <i className="ri-information-line text-info me-2"></i>
                <strong>Total USD alto:</strong> Diagnóstico con mayor impacto financiero
              </li>
              <li className="mb-2">
                <i className="ri-search-line text-primary me-2"></i>
                <strong>Rango muy amplio:</strong> Investigar facturas extremas (min/max)
              </li>
            </ul>
          </div>

          <div className="text-center">
            <Button color="primary" onClick={() => setModalAyuda(false)}>
              <i className="ri-check-line me-1"></i>
              Entendido
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Card>
  );
});

export default DiagnosticosFarmacologicos;
